// src/tabs/messages/BookingRequestsView.jsx — Phase 5.4 Commit 7 (Q-D5)
//
// Vue admin "Demandes directes" sous onglet Communication.
// Liste les direct_booking_requests pending DESC + permet validation/rejet.
//
// Workflow :
//   - GET /api/sites?action=site_booking_requests_list&statut=pending|all
//   - POST /api/sites?action=site_booking_validate body { request_id, action, notes, rejection_reason_public }
//
// Conventions UX :
//   - data-testid pour E2E Playwright
//   - Couleurs DS (navy / sky / red / green selon statut)
//   - Cards par demande avec breakdown tarif
//   - Modal validation pour collecter notes admin

import { useState, useEffect, useCallback } from "react"
import { Mailbox, RefreshCw, Home, Coins, MessageSquare, AlertTriangle, Check, X, Ban } from "lucide-react"
import { supabase } from "../../supabase"
import { DS } from "../../lib/ui"
import { formatDateFR, fmtE } from "../../lib/format"
import { STATUT_COLORS, findLogementNom } from "../../lib/booking-requests-helpers"
// Lot AUTO-REFRESH — realtime « signal → refetch » sur direct_booking_requests
// (une nouvelle demande apparaît sans navigation ni clic « Actualiser »).
import { useRealtimeRefresh } from "../../lib/use-realtime-refresh"
// Lot annulation résa payée — modale extraite en composant PARTAGÉ (aussi
// utilisée par l'onglet Paiements de la modale calendrier). Move byte-identique,
// zéro changement de comportement ici (mêmes props, mêmes data-testid).
import CancelBookingModal from "../../modals/BookingCancelModal"

// Lot annulation résa payée — statuts sur lesquels le bouton « Annuler la
// réservation » apparaît (mêmes statuts que la garde serveur/RPC).
const CANCELLABLE_STATUSES = new Set(['paid_stay', 'paid_caution', 'confirmed'])

// Lot stabilisation 2026-07 (#993 MINEUR-3) : la liste était plafonnée aux 50
// dernières demandes SANS indication → les vieilles demandes étaient
// invisibles (badge pending global ≠ lignes affichées). Pagination simple
// « Afficher plus » par tranches de 50, tri created_at DESC conservé
// (offset serveur + has_more exact).
const PAGE_SIZE = 50

// ouvrirAcceptation / ouvrirRefus (lot 6, props ADDITIVES, précédent
// sansMonthNav) : quand fournies, Valider/Rejeter ouvrent les FEUILLES du
// proto au lieu des modales historiques. Défaut absent = rien ne change.
export default function BookingRequestsView({ cfg, onPendingChange, ouvrirAcceptation, ouvrirRefus }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [requests, setRequests] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [pendingCount, setPendingCount] = useState(0)
  // Micro-UX recette 2026-07 : défaut « Toutes » — le défaut 'pending' masquait
  // les résas payées/confirmées (l'admin ne retrouvait pas sa résa payée).
  // L'option « En attente uniquement » reste disponible dans le sélecteur ;
  // le compteur « X en attente » vient de pending_count (global serveur),
  // indépendant du filtre.
  const [statutFilter, setStatutFilter] = useState('all')
  const [validationModal, setValidationModal] = useState(null) // { request, action }
  const [actionInFlight, setActionInFlight] = useState(false)
  // Lot annulation résa payée — modale d'annulation (CHEMIN ARGENT).
  const [cancelModal, setCancelModal] = useState(null) // { request }

  const fetchPage = useCallback(async (offset) => {
    const { data: sess } = await supabase.auth.getSession()
    const token = sess?.session?.access_token
    if (!token) throw new Error('Pas de session admin active.')
    const res = await fetch(
      `/api/sites?action=site_booking_requests_list&statut=${statutFilter}&limit=${PAGE_SIZE}&offset=${offset}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    const json = await res.json()
    if (!res.ok || !json.ok) throw new Error(json.error || 'fetch_failed')
    return json
  }, [statutFilter])

  // Lot AUTO-REFRESH : `silent=true` (strict, pattern InboxPanel.refresh) =
  // passage AUTO déclenché par le realtime — mêmes lectures, sans basculer
  // loading (pas de clignotement) ; l'échec silencieux garde la dernière vue.
  // Fusion #1012 : le refresh repart toujours de la tranche 1 (offset 0).
  const refresh = useCallback(async (silent) => {
    if (silent !== true) {
      setLoading(true)
      setError(null)
    }
    try {
      const json = await fetchPage(0)
      // Blindage 2026-09-02 : une ligne nulle/malformée dans la réponse ne
      // doit jamais faire tomber tout l'écran — on l'écarte.
      setRequests((json.requests || []).filter(Boolean))
      setHasMore(!!json.has_more)
      setPendingCount(json.pending_count || 0)
      if (onPendingChange) onPendingChange(json.pending_count || 0)
    } catch (e) {
      if (silent !== true) setError(e.message)
      else console.error('[booking-requests] auto-refresh silencieux en échec:', e.message)
    } finally {
      setLoading(false)
    }
  }, [fetchPage, onPendingChange])

  // « Afficher plus » : tranche suivante APPENDUE (tri DESC serveur conservé).
  // Dédup par id : si une demande est arrivée entre deux fetchs, l'offset
  // glisse — on ne ré-affiche jamais deux fois la même ligne.
  const loadMore = useCallback(async () => {
    setLoadingMore(true)
    setError(null)
    try {
      const json = await fetchPage(requests.length)
      const seen = new Set(requests.map((r) => r.id))
      const next = (json.requests || []).filter(Boolean).filter((r) => !seen.has(r.id))
      setRequests([...requests, ...next])
      setHasMore(!!json.has_more)
      setPendingCount(json.pending_count || 0)
      if (onPendingChange) onPendingChange(json.pending_count || 0)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoadingMore(false)
    }
  }, [fetchPage, requests, onPendingChange])

  useEffect(() => { refresh() }, [refresh])

  // Lot AUTO-REFRESH — une demande créée/validée/payée (INSERT/UPDATE serveur)
  // déclenche un refetch silencieux (debounce ≥2 s, pause onglet masqué). Pas
  // de polling ici historiquement : le realtime est purement additif, et le
  // badge App-level (use-notif-counters, 60 s) reste le filet.
  useRealtimeRefresh(
    ["direct_booking_requests"],
    useCallback(() => refresh(true), [refresh]),
  )

  const submitValidation = useCallback(async () => {
    if (!validationModal) return
    setActionInFlight(true)
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) throw new Error('Pas de session admin active.')
      const body = {
        request_id: validationModal.request.id,
        action: validationModal.action,
        notes: validationModal.notes || null,
        rejection_reason_public: !!validationModal.rejection_reason_public,
      }
      const res = await fetch('/api/sites?action=site_booking_validate', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!res.ok || !json.ok) {
        throw new Error(json.error || 'validate_failed')
      }
      setValidationModal(null)
      await refresh()
    } catch (e) {
      alert(`Erreur : ${e.message}`)
    } finally {
      setActionInFlight(false)
    }
  }, [validationModal, refresh])

  return (
    <div data-testid="booking-requests-view" style={{ fontFamily: DS.fontBody }}>
      <div style={{ display: 'flex', gap: 9, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, fontSize: 15 }}>
          <Mailbox size={17} strokeWidth={2} color={DS.marine}/> Demandes directes
        </div>
        <select
          data-testid="booking-requests-statut-filter"
          value={statutFilter}
          onChange={(e) => setStatutFilter(e.target.value)}
          style={{ padding: '7px 9px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.border}`, fontSize: 12, fontFamily: DS.fontBody, color: DS.textPrimary, background: DS.card }}
        >
          <option value="pending">En attente uniquement</option>
          <option value="all">Toutes</option>
        </select>
        <button
          className="fbz-press"
          onClick={refresh}
          disabled={loading}
          data-testid="booking-requests-refresh"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: DS.fontBody }}
        >
          <RefreshCw size={14} strokeWidth={2}/> Actualiser
        </button>
        <div style={{ marginLeft: 'auto', color: DS.textSecondary, fontSize: 12 }}>
          {pendingCount} en attente
        </div>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: DS.dangerBg, color: DS.dangerText, borderRadius: 8, borderLeft: `3px solid ${DS.danger}`, marginBottom: 10, fontSize: 12.5 }}>
          <AlertTriangle size={15} strokeWidth={2} style={{ flexShrink: 0 }}/> {error}
        </div>
      )}

      {loading && <div style={{ color: DS.textSecondary, fontSize: 12 }}>Chargement…</div>}

      {!loading && requests.length === 0 && (
        <div data-testid="booking-requests-empty" style={{ padding: 24, textAlign: 'center', color: DS.textMuted }}>
          {statutFilter === 'pending'
            ? 'Aucune demande en attente pour le moment.'
            : 'Aucune demande pour le moment.'}
        </div>
      )}

      {!loading && requests.map((r) => {
        const sc = STATUT_COLORS[r.statut] || { bg: '#eee', fg: '#000', label: r.statut || 'Statut inconnu' }
        // Demande sans logement rattaché : le dire, pas planter (Dimitri 2026-09-02).
        const logementNom = findLogementNom(cfg, r.logement_id) || 'Sans logement rattaché'
        const breakdown = r.tarif_snapshot_breakdown || null
        return (
          <div
            key={r.id}
            className="fbz-card"
            data-testid={`booking-request-card-${r.id}`}
            style={{
              border: `0.5px solid ${DS.border}`,
              borderRadius: DS.radiusCard,
              padding: 14,
              marginBottom: 10,
              background: DS.card,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 14.5, color: DS.marineDark }}>
                  {r.guest_first_name} {r.guest_last_name}
                </div>
                <div style={{ fontSize: 12, color: DS.textSecondary, wordBreak: 'break-word' }}>
                  {r.guest_email}
                  {r.guest_phone && <span> · {r.guest_phone}</span>}
                </div>
              </div>
              <span
                style={{
                  padding: '3px 10px',
                  borderRadius: 7,
                  background: sc.bg,
                  color: sc.fg,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {sc.label}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, color: DS.textPrimary, flexWrap: 'wrap' }}>
              <Home size={14} strokeWidth={2} color={DS.marine}/> <strong>{logementNom}</strong> · {formatDateFR(r.start_date)} → {formatDateFR(r.end_date)} · {r.nb_voyageurs} voyageur(s)
            </div>

            {r.total_amount_snapshot != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, color: DS.textPrimary, flexWrap: 'wrap' }}>
                <Coins size={14} strokeWidth={2} color={DS.success}/> Tarif estimé : <strong>{fmtE(r.total_amount_snapshot)}</strong>
                {breakdown && (
                  <span style={{ color: DS.textMuted, fontSize: 11 }}>
                    {' '}(Base {fmtE(breakdown.tarif_base || 0)} · Ménage {fmtE(breakdown.cleaning_fee || 0)} · Taxe séjour {fmtE(breakdown.taxe_sejour || 0)})
                  </span>
                )}
              </div>
            )}
            {r.total_amount_snapshot == null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12, color: DS.dangerText }}>
                <AlertTriangle size={14} strokeWidth={2}/> Le tarif n'a pas pu être calculé — il manque le forfait ménage de ce logement (Réglages → Produits).
              </div>
            )}

            {r.message && (
              <div style={{ display: 'flex', gap: 7, background: DS.pageBg, padding: 9, borderRadius: 8, fontSize: 12, color: DS.textPrimary, marginBottom: 8 }}>
                <MessageSquare size={14} strokeWidth={2} color={DS.textMuted} style={{ flexShrink: 0, marginTop: 1 }}/> {r.message}
              </div>
            )}

            <div style={{ fontSize: 11, color: DS.textMuted, marginBottom: 8 }}>
              Reçue le {formatDateFR(r.created_at)} · Expire le {r.expires_at ? formatDateFR(r.expires_at) : 'N/A'}
              {r.validated_by && <span> · Validée par {r.validated_by}</span>}
              {r.rejected_by && <span> · Rejetée par {r.rejected_by}</span>}
            </div>

            {CANCELLABLE_STATUSES.has(r.statut) && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="fbz-press"
                  data-testid={`booking-request-cancel-${r.id}`}
                  onClick={() => setCancelModal({ request: r })}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px',
                    borderRadius: DS.radiusControl,
                    border: `0.5px solid ${DS.borderHover}`,
                    background: DS.card,
                    color: DS.dangerText,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontFamily: DS.fontBody,
                  }}
                >
                  <Ban size={15} strokeWidth={2.4}/> Annuler la réservation
                </button>
              </div>
            )}

            {r.statut === 'pending' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="fbz-press"
                  data-testid={`booking-request-validate-${r.id}`}
                  onClick={() => (ouvrirAcceptation ? ouvrirAcceptation(r) : setValidationModal({ request: r, action: 'validated', notes: '' }))}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px',
                    borderRadius: DS.radiusControl,
                    border: 'none',
                    background: DS.corail,
                    color: '#fff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontFamily: DS.fontBody,
                  }}
                >
                  <Check size={15} strokeWidth={2.4}/> Valider
                </button>
                <button
                  className="fbz-press"
                  data-testid={`booking-request-reject-${r.id}`}
                  onClick={() => (ouvrirRefus ? ouvrirRefus(r) : setValidationModal({ request: r, action: 'rejected', notes: '' }))}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px',
                    borderRadius: DS.radiusControl,
                    border: `0.5px solid ${DS.borderHover}`,
                    background: DS.card,
                    color: DS.dangerText,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontFamily: DS.fontBody,
                  }}
                >
                  <X size={15} strokeWidth={2.4}/> Rejeter
                </button>
              </div>
            )}
          </div>
        )
      })}

      {!loading && hasMore && (
        <div style={{ textAlign: 'center', marginTop: 4, marginBottom: 10 }}>
          <button
            className="fbz-press"
            data-testid="booking-requests-load-more"
            onClick={loadMore}
            disabled={loadingMore}
            style={{ padding: '8px 18px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, cursor: loadingMore ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600, fontFamily: DS.fontBody }}
          >
            {loadingMore ? 'Chargement…' : `Afficher ${PAGE_SIZE} demandes de plus`}
          </button>
        </div>
      )}

      {validationModal && (
        <ValidationModal
          modal={validationModal}
          onChange={(patch) => setValidationModal({ ...validationModal, ...patch })}
          onCancel={() => setValidationModal(null)}
          onConfirm={submitValidation}
          loading={actionInFlight}
        />
      )}

      {cancelModal && (
        <CancelBookingModal
          request={cancelModal.request}
          logementNom={findLogementNom(cfg, cancelModal.request.logement_id)}
          onClose={() => setCancelModal(null)}
          onDone={async () => { setCancelModal(null); await refresh() }}
        />
      )}
    </div>
  )
}

// ── Lot annulation résa payée — modale d'annulation (CHEMIN ARGENT) ──────────
// Extraite en composant PARTAGÉ : src/modals/BookingCancelModal.jsx (importée
// ci-dessus sous son alias historique `CancelBookingModal` — comportement et
// data-testid inchangés ici).

function ValidationModal({ modal, onChange, onCancel, onConfirm, loading }) {
  const isValidate = modal.action === 'validated'
  return (
    <div
      data-testid="booking-validation-modal"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.4)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div className="fbz-modal-sheet" style={{
        background: DS.card, borderRadius: 14, padding: 20, width: '90%', maxWidth: 480,
        fontFamily: DS.fontBody,
      }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 0, fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark }}>
          {isValidate ? <Check size={18} strokeWidth={2.2} color={DS.corail}/> : <X size={18} strokeWidth={2.2} color={DS.dangerText}/>}
          {isValidate ? 'Valider la demande' : 'Rejeter la demande'}
        </h3>
        <div style={{ marginBottom: 12, fontSize: 13, color: DS.textSecondary }}>
          {modal.request.guest_first_name} {modal.request.guest_last_name} · {modal.request.start_date} → {modal.request.end_date}
        </div>

        <label style={{ display: 'block', marginBottom: 8 }}>
          <div style={{ fontSize: 12, marginBottom: 4, color: '#444' }}>
            {isValidate ? 'Notes admin (optionnel)' : 'Motif de rejet'}
          </div>
          <textarea
            data-testid="booking-validation-notes"
            value={modal.notes || ''}
            onChange={(e) => onChange({ notes: e.target.value })}
            rows={4}
            style={{
              width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc',
              fontFamily: DS.fontBody, fontSize: 13,
            }}
            placeholder={isValidate ? 'Notes internes…' : 'Ex : Dates indisponibles'}
          />
        </label>

        {!isValidate && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 12, color: '#444' }}>
            <input
              data-testid="booking-validation-reason-public"
              type="checkbox"
              checked={!!modal.rejection_reason_public}
              onChange={(e) => onChange({ rejection_reason_public: e.target.checked })}
            />
            Inclure le motif dans l'email voyageur (sinon générique)
          </label>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            className="fbz-press"
            onClick={onCancel}
            disabled={loading}
            data-testid="booking-validation-cancel"
            style={{ padding: '8px 16px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.textSecondary, fontWeight: 600, cursor: 'pointer', fontFamily: DS.fontBody }}
          >
            Annuler
          </button>
          <button
            className="fbz-press"
            onClick={onConfirm}
            disabled={loading}
            data-testid="booking-validation-confirm"
            style={{
              padding: '8px 16px', borderRadius: DS.radiusControl, border: isValidate ? 'none' : `0.5px solid ${DS.borderHover}`,
              background: isValidate ? DS.corail : DS.card,
              color: isValidate ? '#fff' : DS.dangerText, fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody,
            }}
          >
            {loading ? '…' : (isValidate ? 'Confirmer la validation' : 'Confirmer le rejet')}
          </button>
        </div>
      </div>
    </div>
  )
}

// Exports internes pour tests (rétro-compat, le helper est aussi dans
// src/lib/booking-requests-helpers.js).
export const _test = { findLogementNom, STATUT_COLORS }
